import { cached, tracked } from '@glimmer/tracking';

import type { DSData } from 'webapp-types';
import {
	BlockVisualization,
	ChangeIndicatorClass,
	ChartLegendPosition,
	DataSelectType,
	OrderDirection,
} from 'webapp/enums/enums';
import { glueDimensionValues } from 'webapp/utils/common';
import { sanitize } from 'webapp/utils/html';
import { formatNumber, getConversionRate, hashCode, sumValues } from 'webapp/utils/number';

import type DataSelect from 'webapp/models-custom/data-select';
import type DsDataOutputCompare from 'webapp/models-custom/ds-data/output/compare';
import DsDataOutputOtherLevel from 'webapp/models-custom/ds-data/output/other';
import type DsDataOutputTopLevel from 'webapp/models-custom/ds-data/output/top';
import VisualizationBase from 'webapp/models-custom/visualization/base';
import DChartComparePoint from 'webapp/d-chart/point/compare-point';
import DChartPrimaryPoint from 'webapp/d-chart/point/primary-point';
import DChartSeries from 'webapp/d-chart/series/series';

interface StepInfo {
	dataSelect?: DataSelect; // when isStepsMetrics
	dimensionValues?: DSData.DimensionsAndValuesMap; // when !isStepsMetrics
	outputs: (DsDataOutputTopLevel | DsDataOutputOtherLevel)[];
	totalPoint: DChartPrimaryPoint;
}

const RecordLimit = 15;

export default class VisualizationFunnel extends VisualizationBase {
	visualizationType = BlockVisualization.FUNNEL;

	recordLimit = RecordLimit;

	// ------- visualization specifics --------

	get firstSliceConversion(): boolean {
		return this.block.firstSliceConversion;
	}

	get hideStepConversion(): boolean {
		return this.block.hideStepConversion;
	}

	get hideOverallConversionRate(): boolean {
		return this.block.hideOverallConversionRate;
	}

	get isStacked(): boolean {
		return this.blockSetup.dataSelectsShown.every((ds) => ds.isStacked);
	}

	get allowOthers() {
		// if isStepsMetricAndDimensionCombos, the "Other" is another step(s)
		// if isStepsMetrics, the "Other" is another flow (one per unit)
		return !!this.block.showOther;
	}

	get isLegendOff(): boolean {
		return this.block.legendPosition === ChartLegendPosition.OFF;
	}

	// ------- visualization - data --------

	@tracked series: DChartSeries[] = [];

	// if user has selected stacked and have multiple metrics, we show these metrics as the steps
	//    and attribute & unit combos as the series
	// when no stacked is selected (or there is just one metric), we have metric & dimension combos as steps
	//    and the units are the series
	get isStepsMetrics() {
		return this.blockSetup.sortedDataSelectsShown.length > 1 && this.isStacked;
	}

	get isStepsMetricAndDimensionCombos() {
		return !this.isStepsMetrics;
	}

	@cached
	get stepCount() {
		return this.sortedSteps.length;
	}

	@cached
	get sortedSteps() {
		const steps: StepInfo[] = [];

		const isStepsMetrics = this.isStepsMetrics,
			dataSelects = this.blockSetup.sortedDataSelectsShown;

		dataSelects.forEach((dataSelect) => {
			const dataRoot = dataSelect.dataRoot;

			if (isStepsMetrics) {
				// group steps by dataSelects, one or more outputs (flows) for each

				const outputs: StepInfo['outputs'] = [];

				let point: DChartPrimaryPoint;
				// we sum all primary values and each compare seperately
				// and calculate changeStr(s) ourselves
				// also must add "Other", if supported
				if (dataRoot) {
					outputs.push(...dataRoot.outputs);

					let value = dataRoot.totalSumValue,
						valueStr = dataRoot.totalSumValueFormatted;

					if (this.allowOthers) {
						outputs.push(...dataRoot.outputsOther);

						let valueFromOther = 0;
						dataRoot.outputsOther.forEach((output) => {
							if (output.pointTotal) {
								valueFromOther += output.pointTotal.value;
							}
						});
						if (valueFromOther) {
							value = (value || 0) + valueFromOther;
							valueStr = formatNumber(value, dataRoot.format, {
								scale: dataRoot.scale,
							});
						}
					}

					point = new DChartPrimaryPoint({
						value,
						valueStr,
						dateRange: dataRoot.dateRange,
					});
					dataRoot.compareRoots.forEach((compareRoot) => {
						const changeStr = compareRoot.totalSumChangeFormatted;
						if (changeStr) {
							const comparePoint = new DChartComparePoint({
								primaryPoint: point,
								value: compareRoot.totalSumValue,
								valueStr: compareRoot.totalSumValueFormatted,
								compareSettings: compareRoot.compareSettings,
								change: compareRoot.totalSumChange,
								changeStr,
								indicatorClass: compareRoot.totalSumIndicatorClass,
								metricName: compareRoot.metricName,
							});
							point.comparePoints.push(comparePoint);
						}
					});
				} else {
					point = new DChartPrimaryPoint();
				}
				point.title = dataRoot?.metricName || dataSelect.shownTitle;

				steps.push({
					dataSelect,
					outputs,
					totalPoint: point,
				});
			} else {
				// group steps by metric + attribute combo, one output per each unit

				if (dataRoot) {
					const outputsPerDimensionValues: {
						dimensionValues: DSData.DimensionsAndValuesMap;
						outputs: StepInfo['outputs'];
					}[] = [];

					dataRoot.outputs.forEach((output) => {
						const attributeOutput = outputsPerDimensionValues.find((item) =>
							output.equalDimensionValues(item.dimensionValues),
						);

						if (attributeOutput) {
							attributeOutput.outputs.push(output);
						} else {
							outputsPerDimensionValues.push({
								dimensionValues: output.dimensionValues,
								outputs: [output],
							});
						}
					});

					if (outputsPerDimensionValues.length) {
						outputsPerDimensionValues.forEach((item) => {
							const firstOutput = item.outputs[0];

							const value = sumValues(item.outputs.map((o) => o.totalSumValue));

							const point = new DChartPrimaryPoint({
								value,
								format: firstOutput?.format,
								scale: firstOutput?.scale,
								title: firstOutput?.shortTitle || dataRoot.metricName,
								titleLong: firstOutput?.fullTitle,
								dateRange: firstOutput?.dateRange,
							});

							// if just one unit ("flow") on this step, we can show the compare too
							if (item.outputs.length === 1) {
								const outputPointCompare = (
									firstOutput!.compareOutputs[0] as DsDataOutputCompare
								)?.point;
								if (outputPointCompare?.changeStr) {
									const comparePoint =
										DChartComparePoint.createFromOutputPointCompare(
											outputPointCompare,
											point,
										);
									point.comparePoints.push(comparePoint);
								}
							}

							steps.push({
								dataSelect,
								dimensionValues: item.dimensionValues,
								outputs: item.outputs,
								totalPoint: point,
							});
						});

						if (this.allowOthers) {
							dataRoot.outputsOther.forEach((output) => {
								if (output.pointTotal) {
									const point = this.createDChartPointFromPoint(
										output,
										output.pointTotal,
									);
									steps.push({
										dataSelect,
										dimensionValues: { other: 'Other' },
										outputs: [output],
										totalPoint: point,
									});
								}
							});
						}
					} else {
						// empty state handling
						const dimensionValues: DSData.DimensionsAndValuesMap = {};
						if (dataSelect.metricSetup.attributeNamesFromBasicFilters.length) {
							dimensionValues.empty =
								dataSelect.metricSetup.attributeNamesFromBasicFilters[0]!;
						}
						const point = new DChartPrimaryPoint({
							title: dataSelect.shownTitle,
							dateRange: dataSelect.dateRange,
						});
						steps.push({
							dataSelect,
							dimensionValues,
							outputs: [],
							totalPoint: point,
						});
					}
				}
			}
		});

		const sortField = this.block.sortField,
			sortOrder = this.block.sortOrder || OrderDirection.DESC;
		steps.sort((a, b) => {
			return DChartPrimaryPoint.doPointCompare(
				a.totalPoint,
				b.totalPoint,
				sortField,
				sortOrder,
			);
		});

		return steps;
	}

	override updateDataCache() {
		this.series = this.getSeries();
		this.updateColorIndexes();
	}

	override clearDataCache() {
		this.series = [];
	}

	updateColorIndexes() {
		const mainSeries = this.series.filter((s) => s.isMain);
		this.updateColorIndexesForItems(mainSeries);
	}

	private getSeries() {
		const series: DChartSeries[] = [],
			stepCount = this.stepCount;

		const sortedSteps = this.sortedSteps,
			isAttributeRelevant = this.isStepsMetrics;

		interface OutputGroup {
			dimensionValues: DSData.DimensionsAndValuesMap | null; // only when isStepsMetrics
			unit: string;
			outputs: (DsDataOutputTopLevel | DsDataOutputOtherLevel | undefined)[];
		}

		const outputGroups: OutputGroup[] = []; // each of this will be a series

		sortedSteps.forEach((stepData, stepIndex) => {
			stepData.outputs.forEach((output) => {
				const existingOption = outputGroups.find((opt) => {
					return (
						opt.unit === output.unit &&
						// cross-metric join across steps: match by dimension value only, since
						// each step's metric may label the same value under a different key
						(!isAttributeRelevant ||
							output.equalAttributeNames(opt.dimensionValues || {}))
					);
				});
				if (existingOption) {
					existingOption.outputs[stepIndex] = output;
				} else {
					const outputs = new Array<OutputGroup['outputs'][number]>(this.stepCount);
					outputs[stepIndex] = output;
					outputGroups.push({
						dimensionValues: isAttributeRelevant ? output.dimensionValues : null,
						unit: output.unit,
						outputs,
					});
				}
			});
		});

		outputGroups.forEach((outputGroupData) => {
			const isOtherGroup = outputGroupData.outputs[0] instanceof DsDataOutputOtherLevel;

			let title = '';
			if (isOtherGroup || isAttributeRelevant) {
				title = isOtherGroup
					? 'Other'
					: glueDimensionValues(outputGroupData.dimensionValues);
				if (outputGroupData.unit) {
					title += ' / ' + outputGroupData.unit;
				}
			} else {
				// isStepsMetricAndDimensionCombos - series must be units
				title = outputGroupData.unit;
				if (!title) {
					if (outputGroups.length > 1) {
						title = 'Empty unit';
					} else {
						title = 'Series'; // default would be Series 1
					}
				}
			}

			const points: DChartPrimaryPoint[] = [];
			for (let i = 0; i < stepCount; i++) {
				const output = outputGroupData.outputs[i],
					dsDataPoint = isOtherGroup
						? (output as DsDataOutputOtherLevel)?.pointTotal
						: (output as DsDataOutputTopLevel)?.point;

				let point;
				if (output) {
					point = this.createDChartPointFromPoint(output, dsDataPoint);
				} else {
					const step = sortedSteps[i]!;
					point = new DChartPrimaryPoint({
						title: step.totalPoint.title,
						titleLong: step.totalPoint.titleLong,
						dateRange: step.totalPoint.dateRange,
					});
				}
				point.index = i;

				points.push(point);
			}

			const serie = new DChartSeries({
				type: DataSelectType.MAIN,
				timezone: this.dataTimezone,
				title,
				typeToDraw: 'streamgraph',
				points,
			});

			series.push(serie);
		});

		if (series.length === 1) {
			// if only one main, we add all compares as series too

			// top level --- all the different compares
			// second level --- step index
			const seriesData: (DsDataOutputCompare | undefined)[][] = [];

			this.blockSetup.sortedDataSelectsShown.forEach((primaryDs) => {
				const step = sortedSteps.find((s) => s.dataSelect === primaryDs);
				if (step) {
					const stepIndex = sortedSteps.indexOf(step);
					primaryDs.dataRoot?.compareRoots.forEach((compareRoot, compareIndex) => {
						// only one per compare
						const output = compareRoot.outputs[0];
						if (output) {
							if (!seriesData[compareIndex]) {
								seriesData[compareIndex] = [];
							}
							seriesData[compareIndex][stepIndex] = output;
						}
					});
				}
			});

			seriesData.forEach((outputs, idx) => {
				const title = `Compare ${idx + 1}`,
					points: DChartPrimaryPoint[] = [];

				for (let i = 0; i < stepCount; i++) {
					const output = outputs[i],
						dsDataPointCompare = output?.point || null;

					let point;
					if (output) {
						point = this.createDChartPointFromPoint(output, dsDataPointCompare);
					} else {
						point = new DChartPrimaryPoint();
					}
					point.index = i;
					point.title = title;

					points.push(point);
				}

				const serie = new DChartSeries({
					type: DataSelectType.COMPARE,
					timezone: this.dataTimezone,
					title,
					typeToDraw: 'streamgraph',
					points,
				});

				series.push(serie);
			});
		}

		return series;
	}

	// ----- helpers for renderer -------

	@cached
	get overallConversionHtml(): string {
		if (this.hideOverallConversionRate) {
			return '';
		}

		const sortedSteps = this.sortedSteps,
			firstStep = sortedSteps[0],
			lastStep = sortedSteps[sortedSteps.length - 1];

		if (!firstStep || !lastStep) {
			return '';
		}

		const currentConversion = getConversionRate(
			firstStep.totalPoint.value,
			lastStep.totalPoint.value,
		);

		// only calculate prev conversion if is dimension steps, OR
		// (metric steps) both first and last DS have first compares of the same type
		let previousConversion;
		if (this.isStepsMetrics) {
			const firstCompare = firstStep.dataSelect?.limitations.compares[0],
				lastCompare = lastStep.dataSelect?.limitations.compares[0];
			if (firstCompare && firstCompare.type === lastCompare?.type) {
				const firstCompareRoot = firstStep.dataSelect?.dataRoot?.compareRoots[0],
					lastCompareRoot = lastStep.dataSelect?.dataRoot?.compareRoots[0];
				if (firstCompareRoot && lastCompareRoot) {
					previousConversion = getConversionRate(
						firstCompareRoot.totalSumValue,
						lastCompareRoot.totalSumValue,
					);
				}
			}
		} else {
			const firstComparePoint = firstStep.totalPoint.comparePoints[0],
				lastComparePoint = lastStep.totalPoint.comparePoints[0];
			if (firstComparePoint && lastComparePoint) {
				previousConversion = getConversionRate(
					firstComparePoint.value,
					lastComparePoint.value,
				);
			}
		}

		const isInverted = !!this.blockSetup.firstShownDataSelect?.dataRoot?.invert;
		let indicatorEl = '';
		if (previousConversion && currentConversion !== previousConversion) {
			const change = Math.round((previousConversion - currentConversion) * 100) / 100,
				nonNegativeChange = Math.abs(change);
			let changeStr = String(nonNegativeChange),
				changeClass = '';

			if (change !== 0) {
				const hasIncrease = change < 0;
				changeStr = (hasIncrease ? '▲' : '▼') + ` ${nonNegativeChange}`;
				changeClass =
					(hasIncrease && !isInverted) || (!hasIncrease && isInverted)
						? ChangeIndicatorClass.POSITIVE
						: ChangeIndicatorClass.NEGATIVE;
			}

			indicatorEl = `<span class="indicator ${changeClass}"><span class="change-str">${changeStr}%</span></span>`;
		}

		return `Overall conversion rate <span class="highcharts-dbx-percent-bubble">${currentConversion}%</span> ${indicatorEl}`;
	}

	calculateStepConversions(values: (number | null)[]): (number | null)[] {
		if (!values.length) {
			return [];
		}

		const firstValue = values[0],
			stepConversions: (number | null)[] = [];

		if (this.firstSliceConversion) {
			let _firstValue = firstValue;

			// in case first value is 0 or null, we take max value from values as first value
			if (values.length && !firstValue) {
				const filteredDataValues = values.filter((value) => value !== null);
				_firstValue = Math.max(...filteredDataValues);
			}

			// from first value - percentage is calculated for every step according to first step.
			values.forEach((value: number | null) => {
				let percent = null;

				if (value !== null && _firstValue) {
					percent = (value / _firstValue) * 100;
					percent = Math.round(percent * 100) / 100;
				}
				stepConversions.push(percent);
			});
		} else {
			// Step by step - percentage is calculated from step and previous step.
			values.forEach((value: number | null, index: number) => {
				let percent = null;
				const prevValue = values[index - 1];

				if (value !== null) {
					if (prevValue) {
						percent = (value / prevValue) * 100;
						percent = Math.round(percent * 100) / 100;
					} else {
						percent = 100;
					}
				}

				stepConversions.push(percent);
			});
		}

		return stepConversions;
	}

	getStepLabelHtml(index: number) {
		const stepInfo = this.sortedSteps[index];

		if (!stepInfo) {
			return '';
		}

		const point = stepInfo.totalPoint;

		let comparesHtml = '';
		point.comparePoints.forEach((comparePoint) => {
			comparesHtml += `<div class="indicator ${comparePoint.indicatorClass}"><span class="change-str">${comparePoint.changeStr}</span></div>`;
		});

		return (
			`<div class="info">` +
			`<div class="name"><strong>${sanitize(point.title)}</strong></div>` +
			`<div class="value">${point.valueStr}</div>` +
			comparesHtml +
			`</div>`
		);
	}

	// ----------- Detecting possible rendering changes -----------

	override get visualizationSettingsHash(): number {
		const block = this.block,
			dss = this.blockSetup.dataSelects;
		return hashCode(
			JSON.stringify({
				flc: block.firstSliceConversion,
				hsc: block.hideStepConversion,
				hocr: block.hideOverallConversionRate,
				l: block.legendPosition,
				sf: block.sortField,
				so: block.sortOrder,
				sho: block.showOther,
				st: dss.map((ds) => ds.isStacked),
				o: dss.map((ds) => ds.order), // bcos of possible custom sort
			}),
		);
	}

	// ------- general helpers --------

	override onCreatePrimaryMainDataSelect(dataSelect: DataSelect): void {
		super.onCreatePrimaryMainDataSelect(dataSelect);

		// if needed, also set stacked on this DS
		const otherDs = Array.from(this.blockSetup.dataSelectsShown).filter((ds) => {
			return ds.model !== dataSelect.model;
		});

		if (otherDs.length && otherDs.every((ds) => ds.isStacked)) {
			dataSelect.isStacked = true;
		}
	}

	static override recordLimit = RecordLimit;
}
